import { createContext, PropsWithChildren, useCallback, useContext } from 'react';
import { useFormattedActiveTaxClassesByMarket, TaxClassOptions } from '@/clients/tax-classes/useTaxClasses.ts';
import { useOrganizationById } from '@/clients/organization/useOrganization.ts';
import { Organization } from '@/clients/organization/Organization.ts';
import { AutomaticProductsFormRules } from '@/shared/forms/AutomaticProductsForm/AutomaticProductsForm.tsx';
import { PartnerOrganizationNames } from '../hooks/useOrganizationNames.ts';

type AutomaticFormContextType = {
    constructionProjectId: string;
    customerOrganization: Organization;
    taxClassOptions: TaxClassOptions[];
    defaultTaxClass: string | undefined;
    defaultValuesLabels?: {
        supplierOrganization?: string;
        carrierOrganization?: string;
        additionalServices?: PartnerOrganizationNames;
    };
    formRules?: AutomaticProductsFormRules;
    handleClearSelection: () => Promise<void>;
};

const AutomaticFormContext = createContext<AutomaticFormContextType | null>(null);

type AutomaticFormProviderProps = PropsWithChildren<{
    constructionProjectId: string;
    customerOrganizationId: number;
    formRules?: AutomaticProductsFormRules;
    onClearSelection: () => Promise<void> | void;
    defaultValuesLabels?: {
        supplierOrganization?: string;
        carrierOrganization?: string;
        additionalServices?: PartnerOrganizationNames;
    };
}>;

export function AutomaticFormProvider({
    children,
    constructionProjectId,
    customerOrganizationId,
    defaultValuesLabels,
    formRules,
    onClearSelection,
}: AutomaticFormProviderProps) {
    const { taxClassOptions, defaultTaxClass } = useFormattedActiveTaxClassesByMarket();
    const { data: customerOrganization } = useOrganizationById(customerOrganizationId);

    const handleClearSelection = useCallback(async () => {
        if (!formRules?.isSelectionClearingDisabled) {
            await onClearSelection();
        }
    }, [formRules?.isSelectionClearingDisabled, onClearSelection]);

    return (
        <AutomaticFormContext.Provider
            value={{
                constructionProjectId,
                defaultValuesLabels,
                taxClassOptions,
                defaultTaxClass,
                customerOrganization,
                formRules,
                handleClearSelection,
            }}
        >
            {children}
        </AutomaticFormContext.Provider>
    );
}

export function useAutomaticFormContext(): AutomaticFormContextType {
    const ctx = useContext(AutomaticFormContext);
    if (ctx === null) {
        throw new Error('useAutomaticFormContext must be used inside <AutomaticProductsForm>');
    }
    return ctx;
}
